// Capa de renderizado de un Questionnaire ya resuelto — extraída de
// MotorFormulario.tsx (Grupo 1) para que el Grupo 2 (024, wizard con firma
// de paciente entre medio) pueda reusar exactamente el mismo render +
// estado (`useQuestionnaireForm`, `ItemRenderer`) SIN el guardado
// automático que hace MotorFormularioCargado (crear QuestionnaireResponse +
// correr el extractor + mostrar PanelFirmaImpresion) — 024 necesita un paso
// intermedio (firma del paciente) entre "enviar el formulario" y "guardar".
// `onSubmit` recibe la respuesta ya limpia (`removeDisabledItems`) y decide
// qué hacer con ella; este componente no sabe ni le importa si se persiste.
import type { Encounter, Patient, Questionnaire, QuestionnaireResponse, Reference } from '@medplum/fhirtypes';
import { removeDisabledItems, useMedplum, useQuestionnaireForm } from '@medplum/react-hooks';
import type { JSX } from 'react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { InsigniaFormularioPropio } from '@/components/InsigniaFormularioPropio';
import { Button } from '@/components/ui/button';
import { ItemRenderer } from './ItemRenderer';

export interface FormularioQuestionnaireProps {
  readonly questionnaire: Questionnaire;
  /** Código del formulario — solo para la insignia de origen (lib/formulariosPropios.ts). Sin él, no se rotula nada. */
  readonly codigo?: string;
  readonly subject: Reference<Patient>;
  readonly encounter?: Reference<Encounter>;
  /** Respuesta existente a precargar — reanudar una nota "in-progress", o mostrar una ya "completed" (con `soloLectura`). Ver MotorFormulario.tsx. */
  readonly defaultValue?: QuestionnaireResponse;
  /**
   * Sin botón de guardar ni `onSubmit` — para reabrir una nota ya
   * "completed" (bug real reportado en vivo 2026-09-03: Notas/Historia no
   * tenían forma de LEER una nota ya creada, solo de crear nuevas). Los
   * campos siguen técnicamente interactivos en el DOM (no se tocó
   * ItemRenderer para sumarle un modo disabled), pero sin acción de guardar
   * cualquier cambio queda inerte — nunca se persiste. No confundir con
   * "reanudar in-progress" (`defaultValue` solo, sin `soloLectura`), que sí
   * guarda vía `onSubmit`.
   */
  readonly soloLectura?: boolean;
  readonly textoBoton?: string;
  readonly textoBotonEnviando?: string;
  readonly onSubmit?: (response: QuestionnaireResponse) => Promise<void>;
  /**
   * Abandona el formulario sin guardarlo — pedido explícito del usuario
   * 2026-09-11: antes la única forma de descartar un formulario en curso era
   * el widget "Formularios pendientes" del Dashboard, no había ningún botón
   * ACÁ, en el propio formulario abierto. Sin `onDiscard` no se muestra el
   * botón (024/consentimientos, que no pasan por acá con guardado directo,
   * siguen sin esta opción).
   */
  readonly onDiscard?: () => Promise<void>;
  readonly textoDescartar?: string;
  readonly textoDescartarEnviando?: string;
}

export function FormularioQuestionnaire({
  questionnaire,
  codigo,
  subject,
  encounter,
  defaultValue,
  soloLectura,
  textoBoton,
  textoBotonEnviando,
  onSubmit,
  onDiscard,
  textoDescartar,
  textoDescartarEnviando,
}: FormularioQuestionnaireProps): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const [enviando, setEnviando] = useState(false);
  const [descartando, setDescartando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDiscard = useCallback(async () => {
    if (!onDiscard || enviando || descartando) return;
    setDescartando(true);
    setError(null);
    try {
      await onDiscard();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setDescartando(false);
    }
  }, [onDiscard, enviando, descartando]);

  const state = useQuestionnaireForm({ questionnaire, subject, encounter, defaultValue });

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (state.loading || enviando || !onSubmit) return;
      setEnviando(true);
      setError(null);
      try {
        const limpio = removeDisabledItems(state.questionnaire, state.questionnaireResponse);
        // `useQuestionnaireForm` arma la respuesta con status "in-progress"
        // (correcto mientras se llena) y nada la pasaba a "completed" al
        // enviar — MotorFormulario.tsx ya tenía un branch entero para notas
        // "completed" (solo lectura, sin volver a pedir firma) que por esto
        // nunca se activaba: toda nota quedaba "in-progress" para siempre,
        // reeditable indefinidamente y mostrada así (en inglés) en
        // Notas/Historia. Bug real reportado en vivo 2026-09-09.
        await onSubmit({ ...limpio, subject, encounter, status: 'completed' });
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setEnviando(false);
      }
    },
    [state, subject, encounter, onSubmit, enviando]
  );

  if (state.loading) {
    return <p className="text-sm text-hce-muted">{t('formulario.cargando')}</p>;
  }

  return (
    <form onSubmit={(e) => void handleSubmit(e)} className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-base font-medium text-hce-ink">{state.questionnaire.title}</h3>
          {codigo && <InsigniaFormularioPropio codigo={codigo} />}
        </div>
        {soloLectura && <span className="text-xs font-mono uppercase tracking-wide text-hce-muted">{t('formulario.soloLectura')}</span>}
      </div>
      {state.items.map((item) => (
        <ItemRenderer
          key={item.linkId}
          item={item}
          hermanos={state.responseItems}
          context={[]}
          questionnaireResponse={state.questionnaireResponse}
          medplum={medplum}
          onChangeAnswer={state.onChangeAnswer}
          onAddGroup={state.onAddGroup}
        />
      ))}
      {error && (
        <Alert variant="red">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {!soloLectura && (
        <div className="flex gap-2">
          <Button type="submit" disabled={enviando || descartando} className="w-fit">
            {enviando ? (textoBotonEnviando ?? t('formulario.guardando')) : (textoBoton ?? t('formulario.guardar'))}
          </Button>
          {onDiscard && (
            <Button type="button" variant="outline" disabled={enviando || descartando} onClick={() => void handleDiscard()} className="w-fit">
              {descartando ? (textoDescartarEnviando ?? t('formulario.descartando')) : (textoDescartar ?? t('formulario.descartar'))}
            </Button>
          )}
        </div>
      )}
    </form>
  );
}
